import React, { useState, useId } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface AnimatedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
}

export const AnimatedTextarea = React.forwardRef<HTMLTextAreaElement, AnimatedTextareaProps>(
  ({ label, className, error, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const generatedId = useId();
    const textareaId = props.id || generatedId;

    return (
      <div className="relative">
        <Textarea
          {...props}
          id={textareaId}
          ref={ref}
          aria-label={label}
          className={cn(
            'min-h-[80px] transition-all duration-200',
            'focus:outline-none focus:ring-0 focus:ring-offset-0',
            isFocused || hasValue ? 'border-blue-400' : '',
            error ? 'border-red-500' : '',
            className
          )}
          style={
            {
              '--tw-ring-offset-shadow': 'none !important',
              '--tw-ring-shadow': 'none !important',
              transition: 'none !important',
            } as React.CSSProperties
          }
          value={props.value}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            setHasValue(e.target.value !== '');
            props.onBlur?.(e);
          }}
          onChange={(e) => {
            setHasValue(e.target.value !== '');
            props.onChange?.(e);
          }}
        />
        <label
          htmlFor={textareaId}
          className={cn(
            'pointer-events-none absolute left-3 px-1 transition-all duration-200',
            isFocused || hasValue
              ? '-top-2.5 bg-background text-xs'
              : 'top-[1.4em] text-sm text-muted-foreground',
            error ? 'text-red-500' : isFocused || hasValue ? 'text-blue-400' : ''
          )}
        >
          <span className="px-1">{label}</span>
        </label>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);

AnimatedTextarea.displayName = 'AnimatedTextarea';
